import { type WorkflowDetailDisplayVariableType } from "#src/routes/workflows/all/[workflowId]/detail/WorkflowDetailKeyValuePanel.helpers";
import { displayValueByDataType } from "#src/utils/display";
import {
  AttributeDataType,
  IdentifierSchema,
  WorkflowVariableDateResolution,
  type LookupAnswerValueType,
  type ResourceLookupType,
} from "@validereinc/domain";
import { DateFormats } from "@validereinc/utilities";
import classNames from "classnames/bind";
import styles from "./WorkflowVariableDisplay.module.scss";

const cx = classNames.bind(styles);

const workflowVariableDisplayAlignment = "right" as const;

const getWorkflowVariableDateDisplayFormat = (
  dateResolution: WorkflowDetailDisplayVariableType["dateResolution"]
) => {
  switch (dateResolution) {
    case WorkflowVariableDateResolution.YEAR:
      return DateFormats.YEAR;
    case WorkflowVariableDateResolution.YEAR_MONTH:
      return DateFormats.FULL_MONTH;
    case WorkflowVariableDateResolution.YEAR_MONTH_DAY:
      return DateFormats.DATE;
    default:
      return undefined;
  }
};

const resolveWorkflowLookupValueForDisplay = (
  variable: WorkflowDetailDisplayVariableType,
  lookupAssetDetailsById: Map<
    string,
    { id: string; name: string; entity_type: ResourceLookupType }
  >
): unknown => {
  if (variable.dataType !== AttributeDataType.LOOKUP) {
    return variable.value;
  }

  const parsedLookupId = IdentifierSchema.safeParse(variable.value);
  if (!parsedLookupId.success || !variable.lookupEntityType) {
    return variable.value;
  }

  const assetDetails = lookupAssetDetailsById.get(parsedLookupId.data);
  if (!assetDetails || assetDetails.entity_type !== variable.lookupEntityType) {
    return variable.value;
  }

  const lookupValue: LookupAnswerValueType = {
    id: assetDetails.id,
    name: assetDetails.name ?? assetDetails.id,
    entity_type: assetDetails.entity_type,
  };

  return lookupValue;
};

/**
 * Renders one workflow variable for a key-value row.
 * Shared by the workflow page and the task drawer.
 */
export const renderWorkflowDisplayVariable = (
  variable: WorkflowDetailDisplayVariableType,
  lookupAssetDetailsById: Map<
    string,
    { id: string; name: string; entity_type: ResourceLookupType }
  >
) => {
  const value = resolveWorkflowLookupValueForDisplay(
    variable,
    lookupAssetDetailsById
  );

  const sharedDisplayOptions = {
    alignment: workflowVariableDisplayAlignment,
    emptyText: "-",
  };

  if (
    variable.dataType === AttributeDataType.MULTI_LOOKUP &&
    variable.lookupEntityType
  ) {
    return displayValueByDataType(value, variable.dataType, {
      ...sharedDisplayOptions,
      lookupEntityType: variable.lookupEntityType,
    });
  }

  if (variable.dataType === AttributeDataType.MULTI_FILE) {
    return displayValueByDataType(value, variable.dataType, {
      ...sharedDisplayOptions,
      className: cx("stacked-files"),
      valueSeparator: "",
    });
  }

  if (variable.dataType === AttributeDataType.DATE) {
    return displayValueByDataType(value, variable.dataType, {
      ...sharedDisplayOptions,
      displayFormat: getWorkflowVariableDateDisplayFormat(
        variable.dateResolution
      ),
    });
  }

  return displayValueByDataType(value, variable.dataType, sharedDisplayOptions);
};
